import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  CheckSquare,
  Check,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  X,
  Loader2,
  Calendar,
  User,
  AlertCircle,
  MessageCircle,
  Send,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { toast } from '../components/ui/Toast';
import { todosApi } from '../api/todos';
import { householdsApi } from '../api/households';
import { useAuthStore } from '../store/authStore';
import type { TodoList, TodoItem, TodoPriority, TodoItemCreate } from '../api/todos';
import type { Household, TodoComment } from '../types';

// =============================================
// Priority helpers
// =============================================
const PRIORITY_ORDER: Record<TodoPriority, number> = { high: 0, medium: 1, low: 2 };

const PriorityDot: React.FC<{ priority: TodoPriority; size?: 'sm' | 'md' }> = ({
  priority,
  size = 'sm',
}) => {
  const sizeClass = size === 'md' ? 'w-3 h-3' : 'w-2.5 h-2.5';
  const colorClass =
    priority === 'high'
      ? 'bg-red-500'
      : priority === 'medium'
      ? 'bg-amber-400'
      : 'bg-neutral-300 dark:bg-neutral-600';
  const label =
    priority === 'high' ? 'Hoch' : priority === 'medium' ? 'Mittel' : 'Niedrig';
  return (
    <span
      className={`${sizeClass} ${colorClass} rounded-full shrink-0 inline-block`}
      title={label}
      aria-label={`Prioritaet: ${label}`}
    />
  );
};

function sortByPriority(items: TodoItem[]): TodoItem[] {
  return [...items].sort(
    (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority],
  );
}

// =============================================
// Due date display
// =============================================
const DueDateBadge: React.FC<{ dateStr: string }> = ({ dateStr }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let colorClass = 'text-neutral-400 dark:text-neutral-500';
  if (diffDays < 0) colorClass = 'text-red-500 dark:text-red-400';
  else if (diffDays === 0) colorClass = 'text-amber-500 dark:text-amber-400';

  const label =
    diffDays < 0
      ? `${Math.abs(diffDays)}T ueberfaellig`
      : diffDays === 0
      ? 'Heute'
      : due.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });

  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${colorClass}`}>
      <Calendar className="w-3 h-3" aria-hidden="true" />
      {label}
    </span>
  );
};

// =============================================
// Avatar / Initial Badge
// =============================================
const InitialBadge: React.FC<{ name: string }> = ({ name }) => {
  const initial = name.charAt(0).toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full
                 bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300
                 text-[10px] font-bold shrink-0"
      title={name}
      aria-label={`Zugewiesen an ${name}`}
    >
      {initial}
    </span>
  );
};

// =============================================
// Progress Bar
// =============================================
const ProgressBar: React.FC<{ done: number; total: number }> = ({ done, total }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="w-full h-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

// =============================================
// Tab Badge
// =============================================
const TabBadge: React.FC<{ count: number }> = ({ count }) => {
  if (count === 0) return null;
  return (
    <span
      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
                 rounded-full bg-primary-500 text-white text-[10px] font-bold shadow-sm ml-1.5"
      aria-label={`${count} offene Aufgaben`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

// =============================================
// Empty State
// =============================================
const EmptyState: React.FC<{
  isHousehold: boolean;
  onCreate: () => void;
}> = ({ isHousehold, onCreate }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center animate-fade-in">
    <div className="w-20 h-20 mb-5 gradient-primary rounded-3xl flex items-center justify-center shadow-glow-primary">
      <CheckSquare className="w-10 h-10 text-white" />
    </div>
    <h2 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
      {isHousehold ? 'Keine Haushalt-Aufgaben' : 'Keine persoenlichen Aufgaben'}
    </h2>
    <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8 max-w-xs">
      {isHousehold
        ? 'Erstelle eine Aufgaben-Liste fuer deinen Haushalt.'
        : 'Erstelle deine erste persoenliche To-do-Liste.'}
    </p>
    <button
      onClick={onCreate}
      aria-label="Neue Aufgaben-Liste erstellen"
      className="w-20 h-20 rounded-full gradient-primary shadow-glow-primary
                 flex items-center justify-center
                 hover:scale-105 active:scale-95 transition-transform duration-200"
    >
      <Plus className="w-10 h-10 text-white" />
    </button>
    <p className="mt-4 text-sm text-neutral-400 dark:text-neutral-500 font-medium">
      Neue Liste erstellen
    </p>
  </div>
);

// =============================================
// List Overview Card
// =============================================
const ListOverviewCard: React.FC<{
  list: TodoList;
  onClick: () => void;
  onDelete: () => void;
}> = ({ list, onClick, onDelete }) => {
  return (
    <div
      style={list.color ? { borderLeft: `4px solid ${list.color}` } : {}}
      className="rounded-2xl overflow-hidden"
    >
    <Card hover onClick={onClick} className="group rounded-l-none">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-primary-50 dark:bg-primary-500/10 text-primary-500 rounded-xl
                        flex items-center justify-center shrink-0 text-2xl
                        group-hover:bg-primary-100 dark:group-hover:bg-primary-500/20 transition-colors">
          {list.icon || <CheckSquare className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
            {list.name}
          </h3>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
            {list.open_count} offen &middot; {list.completed_count} erledigt
          </p>
          <div className="mt-2">
            <ProgressBar done={list.completed_count} total={list.total_count} />
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Liste "${list.name}" loeschen`}
          className="p-2 rounded-lg text-neutral-300 hover:text-red-500 hover:bg-red-50
                     dark:hover:bg-red-500/10 transition-colors shrink-0 opacity-0 group-hover:opacity-100"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </Card>
    </div>
  );
};

// =============================================
// Create List Modal
// =============================================
interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (list: TodoList) => void;
  defaultHousehold?: boolean;
}

const LIST_ICONS = ['\uD83D\uDCCB', '\uD83C\uDFE0', '\uD83D\uDED2', '\uD83D\uDCBC', '\uD83C\uDF93', '\uD83D\uDCAA', '\u2764\uFE0F', '\u2B50', '\uD83C\uDF89'];
const LIST_COLORS = [
  '#6366F1', '#8B5CF6', '#EC4899', '#EF4444',
  '#F59E0B', '#22C55E', '#06B6D4', '#3B82F6',
];

const CreateListModal: React.FC<CreateListModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  defaultHousehold = false,
}) => {
  const [name, setName] = useState('Meine Aufgaben');
  const [icon, setIcon] = useState(LIST_ICONS[0]);
  const [color, setColor] = useState(LIST_COLORS[0]);
  const [useHousehold, setUseHousehold] = useState(defaultHousehold);
  const queryClient = useQueryClient();

  const { data: households } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
  });
  const household = households && households.length > 0 ? households[0] : null;

  const createMutation = useMutation({
    mutationFn: () =>
      todosApi.create({
        name: name.trim() || 'Meine Aufgaben',
        icon: icon || undefined,
        color,
        household_id: useHousehold && household ? household.id : undefined,
      }),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
      toast.success('Liste erstellt');
      onCreated(newList);
      onClose();
      setName('Meine Aufgaben');
      setUseHousehold(defaultHousehold);
    },
    onError: () => {
      toast.error('Fehler', 'Liste konnte nicht erstellt werden.');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Neue Aufgaben-Liste" size="sm">
      <div className="space-y-5">
        {/* Name */}
        <div>
          <label
            htmlFor="todo-list-name"
            className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5"
          >
            Name
          </label>
          <input
            id="todo-list-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createMutation.mutate()}
            autoFocus
            className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       placeholder:text-neutral-400 focus:outline-none focus:ring-2
                       focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200"
          />
        </div>

        {/* Icon picker */}
        <div>
          <p className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-2">
            Icon
          </p>
          <div className="flex flex-wrap gap-2">
            {LIST_ICONS.map((ic, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIcon(ic)}
                className={`w-9 h-9 rounded-xl flex items-center justify-center text-xl transition-all duration-200
                            ${icon === ic
                              ? 'bg-primary-100 dark:bg-primary-500/20 ring-2 ring-primary-500 scale-110'
                              : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                            }`}
                aria-label={`Icon ${ic || 'Standard'}`}
              >
                {ic || <CheckSquare className="w-4 h-4 text-neutral-400" />}
              </button>
            ))}
          </div>
        </div>

        {/* Color picker */}
        <div>
          <p className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-2">
            Farbe
          </p>
          <div className="flex gap-2">
            {LIST_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`w-7 h-7 rounded-full transition-all duration-200 shrink-0
                            ${color === c ? 'ring-2 ring-offset-2 ring-neutral-400 scale-110' : 'hover:scale-105'}`}
                style={{ backgroundColor: c }}
                aria-label={`Farbe ${c}`}
              />
            ))}
          </div>
        </div>

        {/* Household toggle */}
        {household && (
          <div>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                role="switch"
                aria-checked={useHousehold}
                tabIndex={0}
                onClick={() => setUseHousehold(!useHousehold)}
                onKeyDown={(e) => e.key === 'Enter' && setUseHousehold(!useHousehold)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 outline-none
                            focus:ring-2 focus:ring-primary-500/40
                            ${useHousehold
                              ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                              : 'bg-neutral-200 dark:bg-neutral-700'
                            }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200
                               ${useHousehold ? 'translate-x-5' : 'translate-x-0.5'}`}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                  Mit Haushalt teilen
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  {household.name}
                </p>
              </div>
            </label>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="outline" fullWidth onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            fullWidth
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
            disabled={!name.trim()}
          >
            Erstellen
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// =============================================
// Comment Section
// =============================================
interface CommentSectionProps {
  listId: string;
  itemId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ listId, itemId }) => {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: comments = [], isLoading } = useQuery<TodoComment[]>({
    queryKey: ['todo-comments', listId, itemId],
    queryFn: () => todosApi.getComments(listId, itemId),
  });

  const addMutation = useMutation({
    mutationFn: (t: string) => todosApi.addComment(listId, itemId, t),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todo-comments', listId, itemId] });
      setText('');
      inputRef.current?.focus();
    },
    onError: () => {
      toast.error('Fehler', 'Kommentar konnte nicht gespeichert werden.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => todosApi.deleteComment(listId, itemId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todo-comments', listId, itemId] });
    },
    onError: () => {
      toast.error('Fehler', 'Kommentar konnte nicht gelöscht werden.');
    },
  });

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || addMutation.isPending) return;
    addMutation.mutate(trimmed);
  }, [text, addMutation]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="ml-14 mb-3 rounded-xl bg-neutral-50 dark:bg-[#14142a]/70 border border-neutral-100 dark:border-indigo-500/10 animate-fade-in overflow-hidden">
      {/* Kommentarliste */}
      <div className="px-3 pt-3 space-y-2">
        {isLoading && (
          <div className="flex items-center gap-2 py-2 text-neutral-400 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
            Kommentare werden geladen…
          </div>
        )}

        {!isLoading && comments.length === 0 && (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 py-1">
            Noch keine Kommentare. Sei der Erste!
          </p>
        )}

        {comments.map((comment) => {
          const isOwn = currentUser?.id === comment.user_id;
          const initial = comment.user_name.charAt(0).toUpperCase();
          return (
            <div key={comment.id} className="flex items-start gap-2 group/comment">
              {/* Avatar */}
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 mt-0.5
                           bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300
                           text-[10px] font-bold"
                aria-hidden="true"
              >
                {initial}
              </span>

              {/* Inhalt */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-[11px] font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                    {comment.user_name}
                  </span>
                  <span className="text-[10px] text-neutral-400 dark:text-neutral-500 shrink-0">
                    {formatDate(comment.created_at)}
                  </span>
                </div>
                <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-0.5 break-words">
                  {comment.text}
                </p>
              </div>

              {/* Loeschen-Button — nur eigene Kommentare */}
              {isOwn && (
                <button
                  onClick={() => deleteMutation.mutate(comment.id)}
                  disabled={deleteMutation.isPending}
                  aria-label="Kommentar löschen"
                  className="p-1 rounded-md text-neutral-300 dark:text-neutral-600
                             hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10
                             transition-colors shrink-0 opacity-0 group-hover/comment:opacity-100
                             focus:opacity-100"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Eingabezeile */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-neutral-100 dark:border-indigo-500/10 mt-2">
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Kommentar schreiben…"
          aria-label="Neuen Kommentar schreiben"
          className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-neutral-200 dark:border-indigo-500/20
                     bg-white/80 dark:bg-[#1a1a2e]/80 backdrop-blur-sm
                     dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                     focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                     dark:focus:border-indigo-500/40 transition-all duration-200"
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || addMutation.isPending}
          aria-label="Kommentar absenden"
          className="w-7 h-7 rounded-lg gradient-primary text-white flex items-center justify-center shrink-0
                     hover:scale-105 active:scale-95 transition-transform duration-200
                     disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
        >
          {addMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================
// Task Row
// =============================================
interface TaskRowProps {
  item: TodoItem;
  listId: string;
  isToggling: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onEdit: (item: TodoItem) => void;
  isHousehold: boolean;
}

const TaskRow: React.FC<TaskRowProps> = ({
  item,
  listId,
  isToggling,
  onToggle,
  onDelete,
  onEdit,
  isHousehold,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  return (
    <div
      className={`rounded-xl transition-all duration-200 ${
        item.is_completed ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3 px-1 py-2">
        {/* Checkbox — 44px touch target */}
        <button
          onClick={onToggle}
          disabled={isToggling}
          aria-label={
            item.is_completed
              ? `"${item.title}" als offen markieren`
              : `"${item.title}" als erledigt markieren`
          }
          aria-checked={item.is_completed}
          role="checkbox"
          className={`
            w-11 h-11 rounded-xl border-2 flex items-center justify-center shrink-0
            transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30
            ${item.is_completed
              ? 'bg-gradient-to-br from-primary-500 to-secondary-500 border-transparent shadow-md'
              : 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400 dark:hover:border-primary-500 bg-white dark:bg-transparent'
            }
            ${isToggling ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-90'}
          `}
        >
          {isToggling ? (
            <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
          ) : item.is_completed ? (
            <Check className="w-5 h-5 text-white" strokeWidth={3} />
          ) : null}
        </button>

        {/* Content */}
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex-1 min-w-0 text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className={`text-sm font-medium text-neutral-800 dark:text-neutral-100 transition-all duration-300
                          ${item.is_completed ? 'line-through text-neutral-400 dark:text-neutral-500' : ''}`}
            >
              {item.title}
            </p>
            <PriorityDot priority={item.priority} />
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {item.due_date && <DueDateBadge dateStr={item.due_date} />}
            {isHousehold && item.assigned_to_name && (
              <InitialBadge name={item.assigned_to_name} />
            )}
            {item.is_completed && item.completed_by_name && (
              <span className="text-[11px] text-neutral-400 dark:text-neutral-500 flex items-center gap-1">
                <User className="w-3 h-3" aria-hidden="true" />
                {item.completed_by_name}
              </span>
            )}
          </div>
        </button>

        {/* Kommentar-Badge */}
        <button
          onClick={() => setCommentsOpen((v) => !v)}
          aria-label={
            commentsOpen
              ? 'Kommentare schließen'
              : `Kommentare anzeigen${item.comment_count ? ` (${item.comment_count})` : ''}`
          }
          aria-expanded={commentsOpen}
          className={`relative w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors
                      ${commentsOpen
                        ? 'text-primary-500 bg-primary-50 dark:bg-primary-500/10'
                        : 'text-neutral-300 dark:text-neutral-600 hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10'
                      }`}
        >
          <MessageCircle className="w-4 h-4" />
          {!!item.comment_count && item.comment_count > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5
                         rounded-full bg-primary-500 text-white text-[9px] font-bold
                         flex items-center justify-center"
              aria-hidden="true"
            >
              {item.comment_count > 9 ? '9+' : item.comment_count}
            </span>
          )}
        </button>

        {/* Delete */}
        <button
          onClick={onDelete}
          aria-label={`"${item.title}" loeschen`}
          className="w-8 h-8 rounded-lg text-neutral-300 dark:text-neutral-600
                     hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10
                     flex items-center justify-center shrink-0 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Kommentar-Bereich */}
      {commentsOpen && (
        <CommentSection listId={listId} itemId={item.id} />
      )}

      {/* Expanded Details */}
      {expanded && !item.is_completed && (
        <div className="ml-14 mb-2 animate-fade-in">
          <button
            onClick={() => onEdit(item)}
            className="text-xs text-primary-500 hover:text-primary-600 font-medium
                       flex items-center gap-1 px-3 py-1.5 rounded-lg
                       bg-primary-50 dark:bg-primary-500/10 hover:bg-primary-100 dark:hover:bg-primary-500/20
                       transition-colors"
          >
            Details bearbeiten
            <ChevronRight className="w-3 h-3" />
          </button>
          {item.description && (
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400 px-1">
              {item.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// Edit Task Modal
// =============================================
interface EditTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: TodoItem | null;
  listId: string;
  isHousehold: boolean;
  householdMembers: { id: string; name: string }[];
}

const PRIORITY_OPTIONS: { value: TodoPriority; label: string }[] = [
  { value: 'high', label: 'Hoch' },
  { value: 'medium', label: 'Mittel' },
  { value: 'low', label: 'Niedrig' },
];

const EditTaskModal: React.FC<EditTaskModalProps> = ({
  isOpen,
  onClose,
  item,
  listId,
  isHousehold,
  householdMembers,
}) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  useEffect(() => {
    if (item) {
      setTitle(item.title);
      setDescription(item.description || '');
      setPriority(item.priority);
      setDueDate(item.due_date ? item.due_date.slice(0, 10) : '');
      setAssignedTo(item.assigned_to || '');
    }
  }, [item]);

  const updateMutation = useMutation({
    mutationFn: () =>
      todosApi.updateItem(listId, item!.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        due_date: dueDate || null,
        assigned_to: assignedTo || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todo-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
      toast.success('Aufgabe aktualisiert');
      onClose();
    },
    onError: () => {
      toast.error('Fehler', 'Aufgabe konnte nicht gespeichert werden.');
    },
  });

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Aufgabe bearbeiten" size="sm">
      <div className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">
            Titel
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       placeholder:text-neutral-400 focus:outline-none focus:ring-2
                       focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">
            Beschreibung (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       placeholder:text-neutral-400 focus:outline-none focus:ring-2
                       focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200 resize-none"
          />
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">
            Prioritaet
          </label>
          <div className="flex gap-2">
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPriority(opt.value)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold
                            transition-all duration-200 border
                            ${priority === opt.value
                              ? opt.value === 'high'
                                ? 'bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/30 text-red-600 dark:text-red-400'
                                : opt.value === 'medium'
                                ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30 text-amber-600 dark:text-amber-400'
                                : 'bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300'
                              : 'bg-transparent border-neutral-200 dark:border-neutral-700 text-neutral-400 hover:border-neutral-300'
                            }`}
              >
                <PriorityDot priority={opt.value} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Due date */}
        <div>
          <label className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">
            Faelligkeitsdatum (optional)
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                       transition-all duration-200"
          />
        </div>

        {/* Assign to — only for household lists */}
        {isHousehold && householdMembers.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">
              Zuweisen an
            </label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                         bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                         transition-all duration-200"
            >
              <option value="">Niemand zugewiesen</option>
              {householdMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="outline" fullWidth onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            fullWidth
            loading={updateMutation.isPending}
            onClick={() => updateMutation.mutate()}
            disabled={!title.trim()}
          >
            Speichern
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// =============================================
// Priority Cycle Button
// =============================================
const PRIORITY_CYCLE: TodoPriority[] = ['low', 'medium', 'high'];

const PriorityCycleButton: React.FC<{
  priority: TodoPriority;
  onChange: (p: TodoPriority) => void;
}> = ({ priority, onChange }) => {
  const next = PRIORITY_CYCLE[(PRIORITY_CYCLE.indexOf(priority) + 1) % PRIORITY_CYCLE.length];
  const label = priority === 'high' ? 'Hoch' : priority === 'medium' ? 'Mittel' : 'Niedrig';
  const bgClass =
    priority === 'high'
      ? 'bg-red-100 dark:bg-red-500/20 border-red-300 dark:border-red-500/40 text-red-600 dark:text-red-400'
      : priority === 'medium'
      ? 'bg-amber-100 dark:bg-amber-500/20 border-amber-300 dark:border-amber-500/40 text-amber-600 dark:text-amber-400'
      : 'bg-neutral-100 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500';

  return (
    <button
      type="button"
      onClick={() => onChange(next)}
      aria-label={`Prioritaet: ${label} (klicken zum Aendern)`}
      title={`Prioritaet: ${label}`}
      className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0
                  transition-all duration-200 hover:scale-105 active:scale-95 ${bgClass}`}
    >
      <PriorityDot priority={priority} size="md" />
    </button>
  );
};

// =============================================
// Quick Add Input
// =============================================
interface QuickAddInputProps {
  listId: string;
}

const QuickAddInput: React.FC<QuickAddInputProps> = ({ listId }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const addMutation = useMutation({
    mutationFn: (data: TodoItemCreate) => todosApi.addItem(listId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todo-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
      setTitle('');
      setDueDate('');
      setPriority('medium');
      setShowDatePicker(false);
      inputRef.current?.focus();
    },
    onError: () => {
      toast.error('Fehler', 'Aufgabe konnte nicht hinzugefuegt werden.');
    },
  });

  const handleSubmit = useCallback(() => {
    if (!title.trim()) return;
    addMutation.mutate({
      title: title.trim(),
      priority,
      due_date: dueDate || undefined,
    });
  }, [title, priority, dueDate, addMutation]);

  // Format due date for display on the calendar button
  const dueDateLabel = dueDate
    ? new Date(dueDate + 'T00:00:00').toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'short',
      })
    : null;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="Neue Aufgabe..."
          aria-label="Neue Aufgabe hinzufuegen"
          className="flex-1 px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                     bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                     placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                     focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                     dark:focus:border-indigo-500/40 transition-all duration-200"
        />

        {/* Priority toggle */}
        <PriorityCycleButton priority={priority} onChange={setPriority} />

        {/* Date picker toggle */}
        <button
          type="button"
          onClick={() => {
            setShowDatePicker((v) => !v);
            if (!showDatePicker) {
              setTimeout(() => (dateInputRef.current as HTMLInputElement & { showPicker?: () => void })?.showPicker?.(), 50);
            }
          }}
          aria-label={dueDate ? `Datum: ${dueDateLabel}` : 'Faelligkeitsdatum setzen'}
          title={dueDate ? `Datum: ${dueDateLabel}` : 'Faelligkeitsdatum setzen'}
          className={`relative w-11 h-11 rounded-xl border flex items-center justify-center shrink-0
                      transition-all duration-200 hover:scale-105 active:scale-95
                      ${dueDate
                        ? 'bg-primary-100 dark:bg-primary-500/20 border-primary-300 dark:border-primary-500/40 text-primary-600 dark:text-primary-400'
                        : 'bg-neutral-100 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300'
                      }`}
        >
          <Calendar className="w-4 h-4" />
          {dueDate && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary-500 text-white text-[9px] font-bold flex items-center justify-center">
              ✓
            </span>
          )}
        </button>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!title.trim() || addMutation.isPending}
          aria-label="Aufgabe hinzufuegen"
          className="w-11 h-11 rounded-xl gradient-primary shadow-glow-primary text-white
                     flex items-center justify-center shrink-0
                     hover:scale-105 active:scale-95 transition-transform duration-200
                     disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
        >
          {addMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Plus className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Date picker — shown inline when toggled */}
      {showDatePicker && (
        <div className="flex items-center gap-2 px-1 animate-fade-in">
          <Calendar className="w-4 h-4 text-neutral-400 shrink-0" aria-hidden="true" />
          <input
            ref={dateInputRef}
            type="date"
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
              if (e.target.value) setShowDatePicker(false);
            }}
            className="flex-1 px-3 py-2 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                       transition-all duration-200"
          />
          {dueDate && (
            <button
              type="button"
              onClick={() => { setDueDate(''); setShowDatePicker(false); }}
              aria-label="Datum entfernen"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-neutral-400
                         hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// Detail View — Single list
// =============================================
interface DetailViewProps {
  listId: string;
  onBack: () => void;
}

const DetailView: React.FC<DetailViewProps> = ({ listId, onBack }) => {
  const queryClient = useQueryClient();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [editingItem, setEditingItem] = useState<TodoItem | null>(null);

  const { data: list, isLoading, isError } = useQuery<TodoList>({
    queryKey: ['todo-list', listId],
    queryFn: () => todosApi.get(listId),
    refetchInterval: 30_000,
  });

  const { data: households = [] } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
  });

  // Derive household members from Household type (members field)
  const householdMembers = React.useMemo((): { id: string; name: string }[] => {
    if (!list?.is_household || !list.household_id) return [];
    const hh = households.find((h) => h.id === list.household_id);
    if (!hh) return [];
    // HouseholdMember has user_id and user_name fields
    return (hh.members || []).map((m) => ({
      id: m.user_id,
      name: m.user_name || m.user_id,
    }));
  }, [list, households]);

  const toggleMutation = useMutation({
    mutationFn: ({ itemId }: { itemId: string }) =>
      todosApi.toggleItem(listId, itemId),

    onMutate: async ({ itemId }) => {
      setTogglingIds((prev) => new Set(prev).add(itemId));
      await queryClient.cancelQueries({ queryKey: ['todo-list', listId] });
      const previous = queryClient.getQueryData<TodoList>(['todo-list', listId]);

      // Optimistic update
      queryClient.setQueryData<TodoList>(['todo-list', listId], (old) => {
        if (!old) return old;
        const items = old.items.map((i) =>
          i.id === itemId ? { ...i, is_completed: !i.is_completed } : i,
        );
        const completed = items.filter((i) => i.is_completed).length;
        return { ...old, items, completed_count: completed, open_count: items.length - completed };
      });

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['todo-list', listId], ctx.previous);
      }
      toast.error('Fehler', 'Aufgabe konnte nicht aktualisiert werden.');
    },

    onSettled: (_data, _err, { itemId }) => {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['todo-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => todosApi.deleteItem(listId, itemId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ['todo-list', listId] });
      const previous = queryClient.getQueryData<TodoList>(['todo-list', listId]);
      queryClient.setQueryData<TodoList>(['todo-list', listId], (old) => {
        if (!old) return old;
        const items = old.items.filter((i) => i.id !== itemId);
        const completed = items.filter((i) => i.is_completed).length;
        return { ...old, items, total_count: items.length, completed_count: completed, open_count: items.length - completed };
      });
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['todo-list', listId], ctx.previous);
      toast.error('Fehler', 'Aufgabe konnte nicht entfernt werden.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['todo-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
    },
  });

  const clearCompletedMutation = useMutation({
    mutationFn: () => todosApi.clearCompleted(listId),
    onSuccess: (updatedList) => {
      queryClient.setQueryData(['todo-list', listId], updatedList);
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
      toast.success('Erledigte Aufgaben entfernt');
    },
    onError: () => {
      toast.error('Fehler', 'Erledigte konnten nicht entfernt werden.');
    },
  });

  if (isLoading) return <LoadingSpinner fullPage text="Liste wird geladen..." />;

  if (isError || !list) {
    return (
      <Card>
        <p className="text-center py-8 text-red-500">
          Liste konnte nicht geladen werden.
        </p>
      </Card>
    );
  }

  const openItems = sortByPriority(list.items.filter((i) => !i.is_completed));
  const completedItems = list.items.filter((i) => i.is_completed);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          aria-label="Zurueck zur Uebersicht"
          className="p-2 rounded-xl text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200
                     hover:bg-white/60 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {list.icon && <span className="text-xl">{list.icon}</span>}
            <h1
              className="text-xl font-bold text-neutral-800 dark:text-white truncate"
              style={list.color ? { color: list.color } : {}}
            >
              {list.name}
            </h1>
          </div>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            {list.open_count} offen &middot; {list.completed_count} erledigt
          </p>
        </div>
      </div>

      {/* Progress */}
      {list.total_count > 0 && (
        <ProgressBar done={list.completed_count} total={list.total_count} />
      )}

      {/* Quick Add */}
      <Card padding="sm">
        <QuickAddInput listId={listId} />
      </Card>

      {/* Empty */}
      {list.total_count === 0 && (
        <div className="py-12 text-center animate-fade-in">
          <span className="text-5xl block mb-3" aria-hidden="true">
            <CheckSquare className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600" />
          </span>
          <p className="text-neutral-500 dark:text-neutral-400 font-medium">
            Noch keine Aufgaben
          </p>
          <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-1">
            Fuege deine erste Aufgabe oben ein.
          </p>
        </div>
      )}

      {/* Open Tasks */}
      {openItems.length > 0 && (
        <div className="space-y-1">
          {openItems.map((item) => (
            <TaskRow
              key={item.id}
              item={item}
              listId={listId}
              isToggling={togglingIds.has(item.id)}
              onToggle={() => toggleMutation.mutate({ itemId: item.id })}
              onDelete={() => deleteItemMutation.mutate(item.id)}
              onEdit={setEditingItem}
              isHousehold={list.is_household}
            />
          ))}
        </div>
      )}

      {/* Completed Tasks */}
      {completedItems.length > 0 && (
        <div>
          <button
            onClick={() => setCompletedExpanded((e) => !e)}
            className="flex items-center gap-2 w-full px-1 py-2 text-sm font-semibold
                       text-neutral-500 dark:text-neutral-400 hover:text-neutral-700
                       dark:hover:text-neutral-200 transition-colors"
            aria-expanded={completedExpanded}
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                completedExpanded ? '' : '-rotate-90'
              }`}
            />
            {completedItems.length} erledigt
          </button>

          {completedExpanded && (
            <div className="space-y-1 animate-fade-in">
              {completedItems.map((item) => (
                <TaskRow
                  key={item.id}
                  item={item}
                  listId={listId}
                  isToggling={togglingIds.has(item.id)}
                  onToggle={() => toggleMutation.mutate({ itemId: item.id })}
                  onDelete={() => deleteItemMutation.mutate(item.id)}
                  onEdit={setEditingItem}
                  isHousehold={list.is_household}
                />
              ))}
              <div className="pt-2">
                <Button
                  variant="outline"
                  fullWidth
                  loading={clearCompletedMutation.isPending}
                  onClick={() => clearCompletedMutation.mutate()}
                  className="text-red-500 !border-red-200 dark:!border-red-500/20 hover:!bg-red-50 dark:hover:!bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                  Erledigte entfernen ({completedItems.length})
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Task Modal */}
      <EditTaskModal
        isOpen={editingItem !== null}
        onClose={() => setEditingItem(null)}
        item={editingItem}
        listId={listId}
        isHousehold={list.is_household}
        householdMembers={householdMembers}
      />
    </div>
  );
};

// =============================================
// Tab type
// =============================================
type ActiveTab = 'personal' | 'household';

// =============================================
// TodoPage — Root
// =============================================
export const TodoPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ActiveTab>('personal');
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: lists = [], isLoading, isError } = useQuery<TodoList[]>({
    queryKey: ['todo-lists'],
    queryFn: todosApi.list,
  });

  const { data: households = [] } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
    staleTime: 5 * 60 * 1000,
  });

  const hasHousehold = households.length > 0;

  const deleteListMutation = useMutation({
    mutationFn: (id: string) => todosApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
      if (activeListId === id) setActiveListId(null);
      toast.success('Liste geloescht');
    },
    onError: () => {
      toast.error('Fehler', 'Liste konnte nicht geloescht werden.');
    },
  });

  const personalLists = lists.filter((l) => !l.is_household);
  const householdLists = lists.filter((l) => l.is_household);

  const personalOpen = personalLists.reduce((s, l) => s + l.open_count, 0);
  const householdOpen = householdLists.reduce((s, l) => s + l.open_count, 0);

  const visibleLists = activeTab === 'personal' ? personalLists : householdLists;

  // Auto-open single list only on initial load (not on tab switch)
  // We track whether the user has already seen the overview so we don't force-open on tab change
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (!hasAutoOpened.current && visibleLists.length === 1 && activeTab === 'personal') {
      hasAutoOpened.current = true;
      setActiveListId(visibleLists[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // only on mount

  const handleCreated = (list: TodoList) => {
    setActiveListId(list.id);
  };

  // ---- Detail view ----
  if (activeListId) {
    return (
      <>
        <DetailView
          listId={activeListId}
          onBack={() => {
            setActiveListId(null);
            queryClient.invalidateQueries({ queryKey: ['todo-lists'] });
          }}
        />
        <CreateListModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
          defaultHousehold={activeTab === 'household'}
        />
      </>
    );
  }

  if (isLoading) return <LoadingSpinner fullPage text="Aufgaben werden geladen..." />;

  if (isError) {
    return (
      <Card>
        <p className="text-center py-8 text-red-500">
          Daten konnten nicht geladen werden. Bitte versuche es erneut.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">
            Aufgaben
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1 text-sm">
            {personalOpen + householdOpen} offene Aufgaben
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          icon={<Plus className="w-4 h-4" />}
        >
          Neue Liste
        </Button>
      </div>

      {/* Tabs */}
      <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-white/60 dark:bg-[#1e1e2e]/60
                      backdrop-blur-sm border border-white/40 dark:border-indigo-500/10 shadow-sm">
        <button
          onClick={() => {
            setActiveTab('personal');
            setActiveListId(null);
          }}
          className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all duration-200
                      flex items-center
                      ${activeTab === 'personal'
                        ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-md'
                        : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-white/60 dark:hover:bg-white/5'
                      }`}
          aria-pressed={activeTab === 'personal'}
        >
          Persoenlich
          <TabBadge count={personalOpen} />
        </button>

        {hasHousehold && (
          <button
            onClick={() => {
              setActiveTab('household');
              setActiveListId(null);
            }}
            className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all duration-200
                        flex items-center
                        ${activeTab === 'household'
                          ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-md'
                          : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-white/60 dark:hover:bg-white/5'
                        }`}
            aria-pressed={activeTab === 'household'}
          >
            Haushalt
            <TabBadge count={householdOpen} />
          </button>
        )}
      </div>

      {/* No household info */}
      {activeTab === 'household' && !hasHousehold && (
        <Card>
          <div className="flex items-start gap-3 py-2">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                Kein Haushalt vorhanden
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                Erstelle oder trete einem Haushalt bei, um gemeinsame Aufgaben zu verwalten.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {visibleLists.length === 0 && (activeTab === 'personal' || hasHousehold) && (
        <EmptyState
          isHousehold={activeTab === 'household'}
          onCreate={() => setShowCreateModal(true)}
        />
      )}

      {/* List overview */}
      {visibleLists.length > 0 && (
        <div className="space-y-3">
          {visibleLists.map((list) => (
            <ListOverviewCard
              key={list.id}
              list={list}
              onClick={() => setActiveListId(list.id)}
              onDelete={() => deleteListMutation.mutate(list.id)}
            />
          ))}
        </div>
      )}

      <CreateListModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        defaultHousehold={activeTab === 'household'}
      />
    </div>
  );
};
